import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Loader } from "components/Loader";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { useHistory } from "react-router-dom";
import * as Mui from "@mui/material";
import LocalShippingOutlined from "@mui/icons-material/LocalShippingOutlined";
import MonitorHeartOutlined from "@mui/icons-material/MonitorHeartOutlined";
import ReplayRounded from "@mui/icons-material/ReplayRounded";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import StorageOutlined from "@mui/icons-material/StorageOutlined";
import {
  designColors,
  designRadii,
  designType,
} from "services/constants/designTokens";
import {
  GlobalFieldLabel,
  globalTextFieldSx,
} from "components/DesignSystem/sharedUiPatterns";
import { IconSectionCard } from "components/DesignSystem/IconSectionCard";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import {
  DurationField,
  SettingsPageShell,
} from "components/DesignSystem/SettingsPageShell";
import {
  buildSystemDefaults,
  validateSystemSettings,
} from "./punchProcessingRules";
import { useSettingsForm } from "./useSettingsForm";
import { withScreenPermissions } from "components/DesignSystem/ScreenAccess";
import { SCREEN_ACCESS } from "services/utils/screenPermissions";
import { systemPunchProcessingUrl, systemSettingsFromApi, systemSettingsToApi, systemUnitOptionsFromApi } from "./systemPunchProcessingApi";

const retryGroups = [
  { key: "dispatcherRetry", title: "Dispatcher Retry", color: detailAccentPalette.indigo.color, icon: <LocalShippingOutlined fontSize="small" /> },
  { key: "attendanceRetry", title: "Attendance Processing Retry", color: detailAccentPalette.purple.color, icon: <SettingsOutlined fontSize="small" /> },
];

const retentionFields = [
  { key: "hot", label: "Global Event Hot Retention", helperText: "Keep global events in primary database." },
  { key: "archive", label: "Global Event Archive Retention", helperText: "Keep archived events." },
  { key: "rawPayload", label: "Raw Payload Retention", helperText: "Keep raw device payload data." },
  { key: "technicalAudit", label: "Technical Audit Retention", helperText: "Keep technical audit and logs." },
];

const monitoringFields = [
  { key: "deviceInactivity", label: "Device Inactivity Warning", helperText: "Show warning if no communication." },
  { key: "deviceOffline", label: "Device Offline Threshold", helperText: "Mark device as offline." },
  { key: "clockDrift", label: "Clock Drift Warning", helperText: "Show warning for device clock drift." },
  { key: "eventFlowInactivity", label: "Event Flow Inactivity Warning", helperText: "Show warning if no events are received." },
];

// One retry policy (Dispatcher / Attendance Processing): four fields under a
// small titled band.
// Maximum Retry Delay is always in minutes; the other two carry a unit.
const RetryGroup = ({ group, retry, errors, setValue, unitOptions }) => {
  const path = (field) => `${group.key}.${field}`;
  const attemptsLabel = `${group.title}: Maximum Attempts`;
  return (
    <Mui.Box
      sx={{
        height: "100%",
        borderRadius: designRadii.card,
        border: `1px solid ${designColors.slate200}`,
        overflow: "hidden",
      }}
    >
      <Mui.Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          px: 2,
          py: 1.25,
          color: group.color,
          backgroundColor: designColors.slateBg,
          borderBottom: `1px solid ${designColors.slate200}`,
        }}
      >
        {group.icon}
        <Mui.Typography component="h3" sx={designType.calloutTitle}>
          {group.title}
        </Mui.Typography>
      </Mui.Stack>
      <Mui.Grid container spacing={2} sx={{ p: 2 }}>
        <Mui.Grid item xs={12} sm={6}>
          <GlobalFieldLabel>Maximum Attempts</GlobalFieldLabel>
          <Mui.TextField
            type="number"
            size="small"
            value={retry.maxAttempts}
            onChange={(event) => setValue(path("maxAttempts"), event.target.value)}
            error={Boolean(errors[path("maxAttempts")])}
            helperText={errors[path("maxAttempts")]}
            inputProps={{ min: 1, step: 1, "aria-label": attemptsLabel }}
            sx={{ ...globalTextFieldSx, width: "100%", maxWidth: 380 }}
          />
        </Mui.Grid>
        {[
          ["initialDelay", "Initial Retry Delay"],
          ["maxDelay", "Maximum Retry Delay"],
          ["maxAge", "Maximum Retry Age"],
        ].map(([field, label]) => (
          <Mui.Grid item xs={12} sm={6} key={field}>
            <DurationField
              label={label}
              value={retry[field]}
              unitOptions={unitOptions[field]}
              onChange={(value) => setValue(path(field), value)}
              error={errors[path(field)]}
            />
          </Mui.Grid>
        ))}
      </Mui.Grid>
    </Mui.Box>
  );
};

// A row of four fixed-unit duration fields (retention, monitoring).
const DurationGrid = ({ fields, group, values, errors, setValue }) => (
  <Mui.Grid container spacing={2.5}>
    {fields.map((field) => (
      <Mui.Grid item xs={12} sm={6} lg={3} key={field.key}>
        <DurationField
          label={field.label}
          value={values[field.key]}
          onChange={(value) => setValue(`${group}.${field.key}`, value)}
          error={errors[`${group}.${field.key}`]}
          helperText={field.helperText}
        />
      </Mui.Grid>
    ))}
  </Mui.Grid>
);

const ignoreChange = () => {};

function SystemPunchProcessingSettings({ permissions }) {
  // Without the edit permission the settings are shown locked, with no
  // Save or Reset.
  const canEdit = permissions?.canEdit !== false;
  const history = useHistory();
  const { domain } = useSelector(state => state.tokenReducer);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [recordId, setRecordId] = useState(null);
  const [unitOptions, setUnitOptions] = useState(systemUnitOptionsFromApi);
  const { form, errors, isDirty, isSaving, setValue: changeValue, save, reset, replaceValues } = useSettingsForm({
    buildDefaults: buildSystemDefaults,
    validate: validateSystemSettings,
    resetMessage: "Defaults restored. Save Changes to apply them.",
    resetMarksSaved: false,
    successMessage: "System punch processing settings saved.",
    onSave: async values => {
      if (recordId == null) throw new Error("System punch processing settings have no record to update. Reload and try again.");
      const result = await customAPI(systemPunchProcessingUrl(domain, recordId), "PUT", systemSettingsToApi(values));
      if (!result?.ok) return false;
      const data = result.responseDetails;
      return data?.retry_recovery?.dispatcher_retry ? systemSettingsFromApi(data) : values;
    },
  });

  const setValue = canEdit ? changeValue : ignoreChange;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError("");
    customGetAPI(systemPunchProcessingUrl(domain)).then(result => {
      if (!active) return;
      if (result?.ok && result.responseDetails?.retry_recovery?.dispatcher_retry) {
        replaceValues(systemSettingsFromApi(result.responseDetails));
        setRecordId(result.responseDetails.id ?? null);
        setUnitOptions(systemUnitOptionsFromApi(result.responseDetails));
      } else {
        setLoadError(result?.message || "System punch processing settings could not be loaded.");
      }
      setLoading(false);
    }).catch(error => {
      if (!active) return;
      setLoadError(error.message || "System punch processing settings could not be loaded.");
      setLoading(false);
    });
    return () => { active = false; };
  }, [domain, reloadKey, replaceValues]);

  if (loading) return <Loader info="Loading..." />;
  if (loadError) return <Mui.Alert severity="error" sx={{ m: 3 }} action={<Mui.Button color="inherit" onClick={() => setReloadKey(value => value + 1)}>Retry</Mui.Button>}>{loadError}</Mui.Alert>;

  return (
    <SettingsPageShell
      breadcrumb={[
        {
          label: "System Configuration",
          onClick: () => history.push("/admin/support/admin"),
        },
        { label: "System Punch Processing" },
      ]}
      title="Punch Processing Settings"
      subtitle="Configure global settings for punch processing, retry behaviour, data retention and device monitoring."
      isDirty={isDirty}
      isSaving={isSaving}
      onSave={save}
      onReset={reset}
      readOnly={!canEdit}
      resetMessage="This restores the default global punch processing settings in the form. Save Changes to apply them."
    >
      <IconSectionCard
        icon={<ReplayRounded />}
        accent={detailAccentPalette.orange}
        title="Retry & Recovery"
        subtitle="Configure how the system retries failed events and manages quarantine."
      >
        <Mui.Grid container spacing={2}>
          {retryGroups.map((group) => (
            <Mui.Grid item xs={12} lg={6} key={group.key}>
              <RetryGroup
                group={group}
                retry={form[group.key]}
                errors={errors}
                setValue={setValue}
                unitOptions={unitOptions}
              />
            </Mui.Grid>
          ))}
        </Mui.Grid>
        <Mui.Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mt: 2 }}>
          <Mui.Switch
            checked={form.quarantineAfterRetryLimit}
            onChange={(event) => setValue("quarantineAfterRetryLimit", event.target.checked)}
            inputProps={{ "aria-label": "Move events to quarantine after retry limit" }}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: designColors.tealDark },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: designColors.teal,
              },
            }}
          />
          <Mui.Box>
            <Mui.Typography sx={designType.calloutTitle}>
              Move events to quarantine after retry limit
            </Mui.Typography>
            <Mui.Typography sx={designType.calloutText}>
              Events will be moved to quarantine when maximum retry attempts or
              age is reached.
            </Mui.Typography>
          </Mui.Box>
        </Mui.Stack>
      </IconSectionCard>

      <IconSectionCard
        icon={<StorageOutlined />}
        accent={detailAccentPalette.blue}
        title="Data Retention"
        subtitle="Configure how long punch-related data is retained."
      >
        <DurationGrid
          fields={retentionFields}
          group="retention"
          values={form.retention}
          errors={errors}
          setValue={setValue}
        />
      </IconSectionCard>

      <IconSectionCard
        icon={<MonitorHeartOutlined />}
        accent={detailAccentPalette.green}
        title="Device Monitoring"
        subtitle="Configure monitoring thresholds for device health and event flow."
      >
        <DurationGrid
          fields={monitoringFields}
          group="monitoring"
          values={form.monitoring}
          errors={errors}
          setValue={setValue}
        />
      </IconSectionCard>
    </SettingsPageShell>
  );
}

export default withScreenPermissions(SystemPunchProcessingSettings, SCREEN_ACCESS.systemPunchProcessing, "Punch Processing Settings");
